import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Activity } from '@aimmo/contracts';
import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton, cn } from '@aimmo/ui';
import { api, assertOk } from '../lib/api.js';
import { formatDate, formatMonth, formatTime } from '../lib/format.js';
import { isoDay, sameDay } from '../lib/dates.js';
import { queryKeys } from '../lib/queries.js';
import { subjectLink } from '../lib/subjects.js';

const WEEKDAYS = ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'];

/** Monday of the week the first of the month falls in. */
function gridStart(month: Date): Date {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const weekday = (first.getDay() + 6) % 7;
  return new Date(first.getFullYear(), first.getMonth(), 1 - weekday);
}

/**
 * The appointment calendar (docs/domain/08-activities.md).
 *
 * A month grid built from the activities list rather than a calendar library:
 * the only thing this view needs that a table does not give is the shape of a
 * month, and the colours come from `appointment_types` — which is the reason
 * that lookup carries them (M1).
 */
export interface CalendarPageProps {
  /** A day was clicked: `YYYY-MM-DD`. The Agenda opens the event dialog on it. */
  onPickDay?: (date: string) => void;
}

export function CalendarPage({ onPickDay }: CalendarPageProps = {}) {
  const [month, setMonth] = React.useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const start = gridStart(month);
  // Six weeks always: a grid that changes height as you page through the year
  // makes the whole screen jump.
  const days = Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
  const end = new Date(days[41]!);
  end.setHours(23, 59, 59, 999);

  const { data, isPending } = useQuery({
    queryKey: queryKeys.activities.calendar(isoDay(month)),
    queryFn: async ({ signal }) =>
      assertOk(
        await api.activities.list({
          query: {
            page: 1,
            pageSize: 100,
            order: 'asc',
            sort: 'scheduledAt',
            type: 'appointment',
            scheduledFrom: start.toISOString(),
            scheduledTo: end.toISOString(),
          },
          fetchOptions: { signal },
        }),
      ).body,
  });

  const byDay = new Map<string, Activity[]>();
  for (const activity of data?.items ?? []) {
    if (!activity.scheduledAt) continue;
    const key = new Date(activity.scheduledAt).toDateString();
    byDay.set(key, [...(byDay.get(key) ?? []), activity]);
  }

  const today = new Date();
  const shift = (delta: number) =>
    setMonth(new Date(month.getFullYear(), month.getMonth() + delta, 1));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-3">
        <CardTitle className="capitalize">{formatMonth(month)}</CardTitle>
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Mese precedente"
            onClick={() => shift(-1)}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMonth(new Date(today.getFullYear(), today.getMonth(), 1))}
          >
            Oggi
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Mese successivo"
            onClick={() => shift(1)}
          >
            <ChevronRight />
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {isPending && <Skeleton className="h-96 w-full" />}

        {!isPending && (
          <div className="grid grid-cols-7 gap-px overflow-hidden rounded-card bg-border">
            {WEEKDAYS.map((day) => (
              <div key={day} className="bg-surface px-2 py-1 text-label uppercase text-faint">
                {day}
              </div>
            ))}

            {days.map((day) => {
              const entries = byDay.get(day.toDateString()) ?? [];
              const outside = day.getMonth() !== month.getMonth();

              return (
                <div
                  key={day.toISOString()}
                  className={cn(
                    'min-h-24 bg-surface p-1.5',
                    outside && 'bg-surface-sunken',
                    sameDay(day, today) && 'ring-1 ring-inset ring-accent',
                  )}
                >
                  {/* The number is the button: clicking a day is how an event
                      gets created on it (U5), and the appointments inside the
                      cell are links of their own. */}
                  {onPickDay ? (
                    <button
                      type="button"
                      aria-label={`Nuovo evento il ${formatDate(day, 'numeric')}`}
                      className={cn(
                        'rounded-control px-1 text-secondary hover:bg-surface-sunken',
                        outside ? 'text-faint' : 'text-ink-2',
                      )}
                      onClick={() => onPickDay(isoDay(day))}
                    >
                      {day.getDate()}
                    </button>
                  ) : (
                    <span className={cn('text-secondary', outside ? 'text-faint' : 'text-ink-2')}>
                      {day.getDate()}
                    </span>
                  )}

                  <ul className="mt-1 grid gap-1">
                    {entries.map((activity) => (
                      <li key={activity.id}>
                        <Link
                          {...subjectLink(activity.subjectType, activity.subjectId)}
                          className="block truncate rounded-control px-1.5 py-0.5 text-secondary"
                          style={{
                            backgroundColor:
                              activity.appointmentTypeColor ?? 'var(--color-accent-soft)',
                            color:
                              activity.appointmentTypeTextColor ?? 'var(--color-accent-soft-ink)',
                          }}
                          title={`${activity.subjectLabel} — ${activity.title ?? activity.body ?? ''}`}
                        >
                          {formatTime(activity.scheduledAt!)} {activity.subjectLabel}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
